import { type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { DateField, EditButton, Show, useRecordContext } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { NotAuthorized, RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess, type AccessibleRegion } from '../auth/permissions';
import type { Market, User } from '../dataProvider';
import { AdjustBalanceAction } from '../adjustments/AdjustBalanceAction';
import { SupportCreditAction } from '../tickets/SupportCreditAction';
import { formatBalance } from './formatBalance';

/**
 * Users detail (Show) — a single page, consistent with StoreShow (#26) but
 * simpler: a user belongs to ONE market, so there is no per-market fan-out.
 *
 * Editable (on the Edit page): name + tier (D-users-edit). Everything here is
 * otherwise read-only — email, market, the DERIVED balance (market currency),
 * signup date and signup method. The Edit affordance is gated `users:write`, so
 * a viewer never sees it (and the edit route is guarded — see UserEdit).
 *
 * REGION SCOPING (decision 6): a user is shown only if its market is in the
 * viewer's access scope — a single-region user cannot open a user outside their
 * market even via a direct URL. The list is region-scoped already; this is the
 * record-level guard behind it.
 */

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

const TierChip = ({ tier }: { tier: string }) => {
  const plus = tier === 'plus';
  return (
    <Chip
      size="small"
      label={plus ? 'Plus' : 'Free'}
      color={plus ? 'primary' : 'default'}
      variant={plus ? 'filled' : 'outlined'}
    />
  );
};

const UserShowLayout = () => {
  const record = useRecordContext<User>();
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  if (!record) return null;

  // Record-level region guard (decision 6).
  const inScope = accessibleRegions.includes(
    (record.market as Market).toUpperCase() as AccessibleRegion,
  );
  if (!inScope) return <NotAuthorized />;

  const canEdit = hasAccess(
    { role, accessibleRegions },
    { capability: 'users:write' },
  );
  // The manual balance adjustment (D-balance-adjustment, the Phase 5 capstone): the
  // highest-trust write, super_admin-only via balances:adjust, so only a super_admin
  // sees the affordance. The action itself moves value as a ledger entry, never a
  // direct balance write.
  const canAdjust = hasAccess(
    { role, accessibleRegions },
    { capability: 'balances:adjust' },
  );
  // The bounded support credit (D-support-role): a capped positive-only ledger entry
  // the support agent can make from a member detail, distinct from the unbounded
  // super_admin adjustment above.
  const canCredit = hasAccess(
    { role, accessibleRegions },
    { capability: 'balances:credit' },
  );
  // Tracking diagnostics (D-tracking-diagnostics): a strictly read-only per-member trace
  // of the click-to-transaction chain, reached from this member context. Role-gated to
  // support / super_admin like the surface itself, so the link only shows to them. It is
  // navigation, not a mutation.
  const canTrace = hasAccess(
    { role, accessibleRegions },
    { roles: ['support', 'super_admin'] },
  );
  // View as user (D-impersonation): a view-only member's-eye lens, reached from this member
  // context. Role-gated to support / super_admin like the surface itself; navigation only.
  const canImpersonate = hasAccess(
    { role, accessibleRegions },
    { roles: ['support', 'super_admin'] },
  );
  // Consent records (D-consent-records): a compliance oversight view of the member's consent
  // history, reached from this member context. Role-gated to compliance / super_admin like the
  // surface; navigation only, not a mutation.
  const canViewConsent = hasAccess(
    { role, accessibleRegions },
    { roles: ['compliance', 'super_admin'] },
  );

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
              >
                <Typography variant="h5" component="h1">
                  {record.name}
                </Typography>
                {/* Internal ops marker for old-platform migrants (D-legacy-users)
                    — not the consumer-facing acknowledgement. */}
                {record.isLegacy && (
                  <Chip
                    size="small"
                    label="Legacy member"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {record.email}
              </Typography>
              {record.isLegacy && record.legacySignupAt && (
                <Typography variant="caption" color="text.secondary">
                  {`Member since ${new Date(record.legacySignupAt).getFullYear()} (old platform)`}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
              <TierChip tier={record.tier} />
              {canEdit && <EditButton label="Edit" />}
              {canAdjust && <AdjustBalanceAction user={record} />}
              {canCredit && <SupportCreditAction user={record} />}
              {canTrace && (
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to={`/tracking-diagnostics/${record.id}`}
                >
                  Trace activity
                </Button>
              )}
              {canImpersonate && (
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to={`/view-as/${record.id}`}
                >
                  View as user
                </Button>
              )}
              {canViewConsent && (
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to={`/consent/${record.id}`}
                >
                  Consent records
                </Button>
              )}
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
            }}
          >
            <Field label="Market">{record.market.toUpperCase()}</Field>
            <Field label="Balance (derived)">
              {formatBalance(record.balance, record.market)}
            </Field>
            <Field label="Tier">
              <TierChip tier={record.tier} />
            </Field>
            <Field label="Signup method">{record.signupMethod}</Field>
            <Field label="Signup date">
              <DateField source="createdAt" />
            </Field>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

/** The detail view. Exported for testing without the access wrapper. */
export const UserShowView = () => (
  <Show title="User" actions={false} resource="users">
    <UserShowLayout />
  </Show>
);

/** Gated entry point registered as the `users` Resource's show. */
export const UserShow = () => (
  <RequireAccess capability="users:read">
    <UserShowView />
  </RequireAccess>
);
